<template>
  <div class="admin-reports">
    <div
      v-if="!isAdmin"
      class="text-center text-muted py-3"
    >
      No tienes permisos para ver este reporte.
    </div>

    <template v-else>
      <b-row class="align-items-center mb-2">
        <b-col>
          <h1 class="font-weight-bolder mb-0">
            Reportes Mensuales
          </h1>
        </b-col>

        <b-col
          cols="12"
          md="4"
          lg="3"
          class="mt-1 mt-md-0"
        >
          <div class="d-flex align-items-center">
            <b-form-input
              v-model.number="year"
              type="number"
              min="2020"
              step="1"
              class="mr-1"
              @keyup.enter="loadReport"
            />
            <b-button
              variant="primary"
              :disabled="loading"
              @click="loadReport"
            >
              Cargar
            </b-button>
          </div>
          <b-form-checkbox
            v-model="excludeSpecialCustomer"
            switch
            class="mt-1"
            :disabled="loading"
            @change="loadReport"
          >
            Excluir cliente Andres
          </b-form-checkbox>
        </b-col>
      </b-row>

      <b-overlay
        :show="loading"
        opacity="0.12"
      >
        <b-alert
          v-if="errorMessage"
          show
          variant="danger"
          class="mb-2"
        >
          {{ errorMessage }}
        </b-alert>

        <b-card
          v-if="months.length && orderedRows.length"
          no-body
          class="mb-2"
        >
          <div class="table-responsive">
            <table class="table table-sm mb-0 report-table">
              <thead>
                <tr>
                  <th class="label-col">
                    Indicador
                  </th>
                  <th
                    v-for="month in months"
                    :key="`month-header-${month.month}`"
                    class="text-center"
                  >
                    {{ month.label }}
                  </th>
                </tr>
              </thead>

              <tbody>
                <tr
                  v-for="row in orderedRows"
                  :key="row.key"
                  :class="rowColorClass(row.key)"
                >
                  <td class="label-col font-weight-bold">
                    {{ row.label }}
                  </td>
                  <td
                    v-for="month in months"
                    :key="`${row.key}-${month.month}`"
                    class="text-center"
                  >
                    {{ formatCell(row.key, valueFor(row, month.month)) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </b-card>

        <b-alert
          v-else-if="!loading"
          show
          variant="light"
        >
          No hay datos para el año seleccionado.
        </b-alert>

        <b-row v-if="months.length">
          <b-col
            cols="12"
            xl="6"
            class="mb-2"
          >
            <b-card>
              <b-card-title>Estatus Usuarios x Mes</b-card-title>
              <apexchart
                type="line"
                height="300"
                :options="statusLineOptions"
                :series="statusLineSeries"
              />
            </b-card>
          </b-col>

          <b-col
            cols="12"
            xl="6"
            class="mb-2"
          >
            <b-card>
              <b-card-title>Usuarios que cotizaron</b-card-title>
              <apexchart
                type="bar"
                height="300"
                :options="purchaseBarOptions"
                :series="purchaseBarSeries"
              />
            </b-card>
          </b-col>

          <b-col
            cols="12"
            xl="6"
            class="mb-2"
          >
            <b-card>
              <b-card-title>Comisión {{ year }}</b-card-title>
              <apexchart
                type="line"
                height="300"
                :options="commissionLineOptions"
                :series="commissionLineSeries"
              />
            </b-card>
          </b-col>

          <b-col
            cols="12"
            xl="6"
            class="mb-2"
          >
            <b-card>
              <b-card-title>Importe de Venta {{ year }}</b-card-title>
              <apexchart
                type="bar"
                height="300"
                :options="salesBarOptions"
                :series="salesBarSeries"
              />
            </b-card>
          </b-col>
        </b-row>

      </b-overlay>
    </template>
  </div>
</template>

<script>
import axios from '@axios'

function defaultMonthLabel(monthNumber) {
  const labels = [
    'ENERO',
    'FEBRERO',
    'MARZO',
    'ABRIL',
    'MAYO',
    'JUNIO',
    'JULIO',
    'AGOSTO',
    'SEPTIEMBRE',
    'OCTUBRE',
    'NOVIEMBRE',
    'DICIEMBRE',
  ]
  return labels[monthNumber - 1] || `MES ${monthNumber}`
}

export default {
  name: 'AdminReportsMonthly',
  data() {
    return {
      loading: false,
      year: 2026,
      excludeSpecialCustomer: false,
      errorMessage: '',
      reportData: null,
      rowOrder: [
        'activado',
        'retenido',
        'churn',
        'usuarios_que_cotizaron',
        'si_compraron',
        'no_compraron',
        'cotizacion_aprobada',
        'importe_venta',
        'comision',
        'compra_por_cliente',
      ],
    }
  },
  computed: {
    selectedYearNumber() {
      return Number(this.year || new Date().getFullYear())
    },
    visibleMonthLimit() {
      const currentDate = new Date()
      const currentYear = currentDate.getFullYear()

      if (this.selectedYearNumber === currentYear) {
        return currentDate.getMonth() + 1
      }

      return this.maxAvailableMonth
    },
    maxAvailableMonth() {
      const months = Array.isArray(this.reportData?.months) ? this.reportData.months : []
      return months.reduce((max, month) => Math.max(max, Number(month?.month) || 0), 0)
    },
    isAdmin() {
      const user = this.$store.getters['auth/currentUser'] || {}
      return user.role_name === 'admin'
    },
    months() {
      const payloadMonths = Array.isArray(this.reportData?.months) ? this.reportData.months : []
      const monthsByNumber = payloadMonths.reduce((acc, month) => {
        const monthNumber = Number(month?.month)
        if (!monthNumber) return acc
        acc[monthNumber] = month
        return acc
      }, {})

      const limit = Number(this.visibleMonthLimit || 0)
      if (!limit) return []

      const normalizedMonths = []
      for (let monthNumber = 1; monthNumber <= limit; monthNumber += 1) {
        const existingMonth = monthsByNumber[monthNumber]
        if (existingMonth) {
          normalizedMonths.push(existingMonth)
        } else {
          normalizedMonths.push({
            month: monthNumber,
            label: defaultMonthLabel(monthNumber),
          })
        }
      }
      return normalizedMonths
    },
    monthLabels() {
      return this.months.map(month => month.label)
    },
    rowsMap() {
      const rows = Array.isArray(this.reportData?.rows) ? this.reportData.rows : []
      return rows.reduce((acc, row) => {
        if (row?.key) acc[row.key] = row
        return acc
      }, {})
    },
    orderedRows() {
      const rows = []
      const used = new Set()

      this.rowOrder.forEach(key => {
        const row = this.rowsMap[key]
        if (row) {
          rows.push(row)
          used.add(key)
        }
      })

      Object.keys(this.rowsMap).forEach(key => {
        if (!used.has(key)) rows.push(this.rowsMap[key])
      })

      return rows
    },
    statusLineSeries() {
      const keys = ['activado', 'retenido', 'churn']
      return keys.map(key => ({
        name: this.rowsMap[key]?.label || key,
        data: this.months.map(month => this.valueFor(this.rowsMap[key], month.month)),
      }))
    },
    purchaseBarSeries() {
      const keys = ['si_compraron', 'no_compraron']
      return keys.map(key => ({
        name: this.rowsMap[key]?.label || key,
        data: this.months.map(month => this.valueFor(this.rowsMap[key], month.month)),
      }))
    },
    commissionLineSeries() {
      return [
        {
          name: this.rowsMap.comision?.label || 'Comisión',
          data: this.months.map(month => this.valueFor(this.rowsMap.comision, month.month)),
        },
      ]
    },
    salesBarSeries() {
      return [
        {
          name: this.rowsMap.importe_venta?.label || 'Importe de Venta',
          data: this.months.map(month => this.valueFor(this.rowsMap.importe_venta, month.month)),
        },
      ]
    },
    statusLineOptions() {
      return {
        chart: {
          toolbar: { show: false },
          zoom: { enabled: false },
        },
        stroke: {
          curve: 'smooth',
          width: 3,
        },
        markers: { size: 4 },
        colors: ['#74c043', '#00c0ef', '#ea5455'],
        xaxis: {
          categories: this.monthLabels,
        },
        yaxis: {
          min: 0,
          forceNiceScale: true,
          labels: {
            formatter: value => Math.round(value),
          },
        },
        dataLabels: {
          enabled: false,
        },
        legend: {
          position: 'top',
        },
      }
    },
    purchaseBarOptions() {
      return {
        chart: {
          toolbar: { show: false },
          stacked: false,
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '45%',
          },
        },
        colors: ['#74c043', '#5d97c9'],
        xaxis: {
          categories: this.monthLabels,
        },
        yaxis: {
          min: 0,
          forceNiceScale: true,
          labels: {
            formatter: value => Math.round(value),
          },
        },
        dataLabels: {
          enabled: false,
        },
        legend: {
          position: 'top',
        },
      }
    },
    commissionLineOptions() {
      return {
        chart: {
          toolbar: { show: false },
          zoom: { enabled: false },
        },
        stroke: {
          curve: 'smooth',
          width: 3,
        },
        colors: ['#5a5a5a'],
        xaxis: {
          categories: this.monthLabels,
        },
        yaxis: {
          min: 0,
          forceNiceScale: true,
          labels: {
            formatter: value => this.money(value),
          },
        },
        dataLabels: {
          enabled: false,
        },
      }
    },
    salesBarOptions() {
      return {
        chart: {
          toolbar: { show: false },
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '40%',
          },
        },
        colors: ['#111111'],
        xaxis: {
          categories: this.monthLabels,
        },
        yaxis: {
          min: 0,
          forceNiceScale: true,
          labels: {
            formatter: value => this.money(value),
          },
        },
        dataLabels: {
          enabled: true,
          formatter: value => this.money(value),
          offsetY: -6,
          style: {
            fontSize: '11px',
            colors: ['#333333'],
          },
        },
      }
    },
  },
  created() {
    if (this.isAdmin) {
      this.loadReport()
    }
  },
  methods: {
    valueFor(row, monthNumber) {
      if (!row || !Array.isArray(row.months)) return 0
      const found = row.months.find(item => Number(item.month) === Number(monthNumber))
      return Number(found?.value || 0)
    },
    money(value) {
      return `$${Number(value || 0).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`
    },
    formatCell(key, value) {
      if (key === 'importe_venta' || key === 'comision') return this.money(value)
      if (key === 'compra_por_cliente') {
        return Number(value || 0).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      }
      return Math.round(Number(value || 0))
    },
    rowColorClass(key) {
      if (key === 'activado') return 'row-activado'
      if (key === 'retenido') return 'row-retenido'
      if (key === 'churn') return 'row-churn'
      if (key === 'usuarios_que_cotizaron') return 'row-cotizaron'
      return ''
    },
    async loadReport() {
      this.loading = true
      this.errorMessage = ''

      try {
        const selectedYear = Number(this.year || 2026)
        const params = { year: selectedYear }
        if (this.excludeSpecialCustomer) {
          params.exclude_special_customer = true
        }

        const { data } = await axios.get('/v1/reports/quotes_purchased_lifecycle_monthly', {
          params,
        })
        this.reportData = data || {}
      } catch (error) {
        const responseData = error?.response?.data || {}
        this.errorMessage = responseData.message || responseData.error || 'No se pudo cargar el reporte mensual'
        this.reportData = null
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.admin-reports {
  display: flex;
  flex-direction: column;
}

.report-table {
  th,
  td {
    vertical-align: middle;
    border: 1px solid #d9d9d9;
  }

  th {
    font-weight: 700;
  }
}

.label-col {
  min-width: 230px;
  text-align: left;
}

.row-activado {
  background-color: #c9deab;
}

.row-retenido {
  background-color: #bdd5ee;
}

.row-churn {
  background-color: #f5ceb1;
}

.row-cotizaron {
  background-color: #ececec;
}
</style>
